import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ProviderProvider } from "./context/ProviderContext";
import PrivateRoute from "./components/PrivateRoute";
import Dashboard from "./pages/Dashboard";
import Home from "./pages/Home";
import OrderServer from "./pages/OrderServer";
import OrderSpace from "./pages/OrderSpace";
import SpacesDashboard from "./pages/Spaces/SpacesDashboard";
import ServersDashboard from "./pages/Servers/ServersDashboard";
import Firewalls from "./pages/Firewalls";
import { Toaster } from "react-hot-toast";
import Console from "./pages/Console";
import AdminLogin from "./pages/Admin/Login";
import AdminLayout from "./components/Admin/AdminLayout";
import AdminDashboard from "./pages/Admin/Dashboard";
import PricingSettings from "./pages/Admin/Pricing";
import VisibilitySettings from "./pages/Admin/Visibility";
import MenuManager from "./pages/Admin/MenuManager";
import AdminUsers from "./pages/Admin/Users";
import UserDetail from "./pages/Admin/UserDetail";
import CronLogs from "./pages/Admin/CronLogs";
import Login from "./pages/Login";
import PaymentStatus from "./pages/PaymentStatus";
import Loading from "./components/Loading";
import { useAuth } from "./context/AuthContext";

const ServerDetails = lazy(() => import("./pages/ServerDetails"));
const SpaceDetails = lazy(() => import("./pages/Spaces/SpaceDetails"));

const RootRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading text="Loading experience..." fullHeight />;
  }

  return user ? <Dashboard /> : <Home />;
};

function App() {
  return (
    <AuthProvider>
      <ProviderProvider>
        <Toaster toastOptions={{ position: "top-center", duration: 5000 }} />
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route
                path="/admin/settings/pricing"
                element={<PricingSettings />}
              />
              <Route
                path="/admin/settings/visibility"
                element={<VisibilitySettings />}
              />
              <Route path="/admin/settings/menu" element={<MenuManager />} />
              <Route path="/admin/cron/logs" element={<CronLogs />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/users/:id" element={<UserDetail />} />
            </Route>
            <Route path="/order" element={<OrderServer />} />
            <Route path="/spaces/order" element={<OrderSpace />} />
            <Route
              path="/servers"
              element={
                <PrivateRoute>
                  <ServersDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/spaces"
              element={
                <PrivateRoute>
                  <SpacesDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/spaces/:id"
              element={
                <PrivateRoute>
                  <Suspense fallback={<Loading text="Loading space..." />}>
                    <SpaceDetails />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute>
                  <PaymentStatus />
                </PrivateRoute>
              }
            />
            <Route
              path="/firewalls"
              element={
                <PrivateRoute>
                  <Firewalls />
                </PrivateRoute>
              }
            />
            <Route
              path="/server/:id"
              element={
                <PrivateRoute>
                  <Suspense fallback={<Loading text="Loading server..." />}>
                    <ServerDetails />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/server/:id/console"
              element={
                <PrivateRoute>
                  <Console />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<RootRoute />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </ProviderProvider>
    </AuthProvider>
  );
}

export default App;
